import { useTranslation } from "react-i18next";

/* global Blob */
import React, { useState } from 'react';
import { Container, Input, Button, Form, FormGroup, Col, Row, Table, Card, CardBody, Badge } from 'reactstrap';
import Switch from 'react-switch';
import Layout from 'Components/Layout';
import { ModernAPI } from 'Modules/ModernAPI';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';

function parseVCardText(vCardText) {
  if (!vCardText) return [];
  const contacts = [];
  const rawCards = vCardText.split(/BEGIN:VCARD/i).filter(c => c && c.trim().length > 0);

  for (const raw of rawCards) {
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const contact = {
      name: '',
      company: '',
      title: '',
      phones: [],
      emails: [],
      addresses: [],
      birthday: '',
      notes: []
    };

    for (const line of lines) {
      if (line.startsWith('FN:') || line.startsWith('FN;')) {
        contact.name = line.substring(line.indexOf(':') + 1).replace(/\\,/g, ',').replace(/\\;/g, ';');
      } else if (!contact.name && (line.startsWith('N:') || line.startsWith('N;'))) {
        const parts = line.substring(line.indexOf(':') + 1).split(';').filter(Boolean);
        contact.name = parts.reverse().join(' ').trim();
      } else if (line.startsWith('TEL') && line.includes(':')) {
        const num = line.substring(line.indexOf(':') + 1);
        const typeMatch = line.match(/TYPE=([^:;]+)/i);
        contact.phones.push({ number: num, type: typeMatch ? typeMatch[1] : 'Phone' });
      } else if (line.startsWith('EMAIL') && line.includes(':')) {
        const email = line.substring(line.indexOf(':') + 1);
        const typeMatch = line.match(/TYPE=([^:;]+)/i);
        contact.emails.push({ email, type: typeMatch ? typeMatch[1] : 'Email' });
      } else if ((line.startsWith('ORG:') || line.startsWith('ORG;')) && line.includes(':')) {
        contact.company = line.substring(line.indexOf(':') + 1).replace(/;/g, ' - ').replace(/\\,/g, ',');
      } else if ((line.startsWith('TITLE:') || line.startsWith('TITLE;')) && line.includes(':')) {
        contact.title = line.substring(line.indexOf(':') + 1);
      } else if (line.startsWith('ADR') && line.includes(':')) {
        const addrParts = line.substring(line.indexOf(':') + 1).split(';').filter(Boolean);
        contact.addresses.push(addrParts.join(', '));
      } else if (line.startsWith('BDAY:') && line.includes(':')) {
        contact.birthday = line.substring(line.indexOf(':') + 1);
      } else if (line.startsWith('NOTE:') && line.includes(':')) {
        contact.notes.push(line.substring(line.indexOf(':') + 1).replace(/\\n/g, '\n'));
      }
    }

    if (contact.name || contact.phones.length > 0 || contact.emails.length > 0 || contact.company) {
      contacts.push(contact);
    }
  }

  return contacts;
}

export default (() => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [backupFlagLatest, setBackupFlag] = useState(false);
  const [error, setError] = useState(null);

  // Decrypted contacts viewer state
  const [decryptedVCardText, setDecryptedVCardText] = useState(null);
  const [parsedContacts, setParsedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'raw'

  const downloadTxtFile = (data, version) => {
    const element = document.createElement('a');
    const file = new Blob([data], {
      type: 'text/vcard;charset=utf-8'
    });
    element.href = URL.createObjectURL(file);
    element.download = `hmi_contacts_${version}.vcf`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadRawEncryptedFile = (encryptedData, version) => {
    const element = document.createElement('a');
    const file = new Blob([encryptedData], {
      type: 'text/plain;charset=utf-8'
    });
    element.href = URL.createObjectURL(file);
    element.download = `hmi_contacts_${version}.enc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const decryptDataWithPassword = async () => {
    const version = backupFlagLatest ? 'latest' : 'initial';
    const backupResult = await ModernAPI.getContactsBackup({
      version
    });

    if (!backupResult || (!backupResult.data && !backupResult.encryptedData)) {
      throw new Error(t("No backup file found for this version. Please create a backup using the mobile app first."));
    }

    const data = backupResult.data || backupResult.encryptedData;

    let vCards = null;
    try {
      vCards = AES.decrypt(data, password).toString(Utf8);
    } catch (e) {
      throw new Error(t("Incorrect password. Please verify your backup passcode and try again."));
    }

    if (!vCards || (!vCards.includes('VCARD') && !vCards.includes('BEGIN:'))) {
      throw new Error(t("Incorrect password. Please verify your backup passcode and try again."));
    }

    return { vCards, version, rawEncrypted: data };
  };

  const handleViewContacts = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { vCards } = await decryptDataWithPassword();
      setDecryptedVCardText(vCards);
      const contacts = parseVCardText(vCards);
      setParsedContacts(contacts);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || t("Unexpected error while decrypting backup data."));
      setLoading(false);
    }
  };

  const handleDownloadDecryptedFile = async () => {
    if (decryptedVCardText) {
      const version = backupFlagLatest ? 'latest' : 'initial';
      downloadTxtFile(decryptedVCardText, version);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { vCards, version } = await decryptDataWithPassword();
      downloadTxtFile(vCards, version);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || t("Unexpected error while loading backup data."));
      setLoading(false);
    }
  };

  const handleDownloadEncryptedOnly = async () => {
    setLoading(true);
    setError(null);
    try {
      const version = backupFlagLatest ? 'latest' : 'initial';
      const backupResult = await ModernAPI.getContactsBackup({ version });

      if (!backupResult || (!backupResult.data && !backupResult.encryptedData)) {
        setError(t("No backup found for this version. Please back up your contacts in the mobile app first."));
        setLoading(false);
        return;
      }

      downloadRawEncryptedFile(backupResult.data || backupResult.encryptedData, version);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(t("Unexpected error while loading backup data. Please try again later."));
      setLoading(false);
    }
  };

  const filteredContacts = parsedContacts.filter(c => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(term)) ||
      (c.company && c.company.toLowerCase().includes(term)) ||
      (c.title && c.title.toLowerCase().includes(term)) ||
      (c.phones && c.phones.some(p => p.number && p.number.toLowerCase().includes(term))) ||
      (c.emails && c.emails.some(em => em.email && em.email.toLowerCase().includes(term)))
    );
  });

  return <Layout>
      <Container className="py-4">
        <h2>{t("User Contacts Backup")}</h2>
        <div className="alert alert-warning my-3" role="alert">
          <strong>{t("Zero-Knowledge Encryption:")} </strong>
          {t("Your contacts backup is encrypted on your mobile device using your personal password. HMI never stores this password and has no access to your decrypted contacts. If you forget your password, HMI cannot restore your data.")}
        </div>
        <p className="text-muted">
          {t("View and search your contacts directly in your browser, download the decrypted .vcf file, or export the raw encrypted file.")}
        </p>
        <Form className='form mt-4' onSubmit={handleViewContacts}>
          <h5>{t("Choose Backup Version")}</h5>
          <Row className="mb-3">
            <Col xs='12'>
              <label className='d-flex align-items-center'>
                <span className='mr-3 font-weight-bold'>{t("Initial Backup")}</span>
                <Switch onChange={flag => {
                  setBackupFlag(flag);
                  setDecryptedVCardText(null);
                  setParsedContacts([]);
                }} checked={backupFlagLatest} onColor='#00A956' handleDiameter={20} uncheckedIcon={false} checkedIcon={false} boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)' activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)' height={16} width={42} className='react-switch mr-3' />
                <span className='font-weight-bold'>{t("Most Recent Backup")}</span>
              </label>
            </Col>
          </Row>
          
          <h5 className="mt-3">{t("Enter your backup password")}</h5>
          <FormGroup>
            <Input type='password' placeholder={t("Enter secret backup password")} value={password} onChange={event => {
            setPassword(event.target.value);
          }} />
          </FormGroup>

          <div className="d-flex flex-wrap gap-2 mt-3" style={{ gap: '10px' }}>
            <Button color='primary' type='submit' disabled={loading || password === ''}>
              {loading ? t("Decrypting...") : t("View Contacts Online")}
            </Button>
            <Button color='success' type='button' onClick={handleDownloadDecryptedFile} disabled={loading || password === ''}>
              {t("Download .vcf File")}
            </Button>
            <Button color='secondary' outline type='button' onClick={handleDownloadEncryptedOnly} disabled={loading}>
              {t("Download Raw Encrypted (.enc)")}
            </Button>
          </div>
          {error && <p className='text-danger mt-3 font-weight-bold'>{error}</p>}
        </Form>

        {/* Decrypted Contacts Viewer Section */}
        {decryptedVCardText && (
          <Card className="mt-5 shadow-sm">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                  <h4 className="mb-1">{t("Decrypted Contacts")} <Badge color="info">{parsedContacts.length}</Badge></h4>
                  <small className="text-muted">{t("Contacts decrypted locally in your web browser.")}</small>
                </div>
                <div className="d-flex gap-2 align-items-center mt-2 mt-md-0" style={{ gap: '8px' }}>
                  <Button
                    size="sm"
                    color={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('table')}
                  >
                    {t("Table View")}
                  </Button>
                  <Button
                    size="sm"
                    color={viewMode === 'raw' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('raw')}
                  >
                    {t("Raw VCF Text")}
                  </Button>
                  <Button
                    size="sm"
                    color="success"
                    onClick={() => downloadTxtFile(decryptedVCardText, backupFlagLatest ? 'latest' : 'initial')}
                  >
                    {t("Save .vcf")}
                  </Button>
                </div>
              </div>

              {viewMode === 'table' ? (
                <>
                  <FormGroup className="mb-3">
                    <Input
                      type="text"
                      placeholder={t("Search by name, email, phone, or company...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormGroup>

                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table hover striped className="mb-0">
                      <thead className="thead-light" style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th>#</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Numbers")}</th>
                          <th>{t("Emails")}</th>
                          <th>{t("Organization / Title")}</th>
                          <th>{t("Addresses")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContacts.length > 0 ? (
                          filteredContacts.map((contact, index) => (
                            <tr key={index}>
                              <td className="text-muted">{index + 1}</td>
                              <td className="font-weight-bold">{contact.name || <em>{t("Unnamed")}</em>}</td>
                              <td>
                                {contact.phones && contact.phones.length > 0 ? (
                                  contact.phones.map((p, pIdx) => (
                                    <div key={pIdx}>
                                      <a href={`tel:${p.number}`}>{p.number}</a>
                                      {p.type && <small className="text-muted ml-1">({p.type})</small>}
                                    </div>
                                  ))
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {contact.emails && contact.emails.length > 0 ? (
                                  contact.emails.map((em, eIdx) => (
                                    <div key={eIdx}>
                                      <a href={`mailto:${em.email}`}>{em.email}</a>
                                    </div>
                                  ))
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {contact.company && <div><strong>{contact.company}</strong></div>}
                                {contact.title && <div className="text-muted">{contact.title}</div>}
                                {!contact.company && !contact.title && <span className="text-muted">-</span>}
                              </td>
                              <td>
                                {contact.addresses && contact.addresses.length > 0 ? (
                                  contact.addresses.map((addr, aIdx) => (
                                    <small key={aIdx} className="d-block">{addr}</small>
                                  ))
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              {searchTerm ? t("No contacts match your search query.") : t("No contacts found in this backup.")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <div>
                  <textarea
                    readOnly
                    className="form-control font-monospace"
                    style={{ height: '400px', fontSize: '13px', backgroundColor: '#1e1e1e', color: '#00ff66' }}
                    value={decryptedVCardText}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </Layout>;
});